import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import GAService from '../../services/GAService';

// Site-wide announcement strip above the header (Webflow banner7). For a new
// announcement, update announcement.* in the locale bundles, bump
// ANNOUNCEMENT_ID to re-show it to visitors who dismissed a previous one, and
// set ANNOUNCEMENT_ENDS_AT, or it won't show at all.
// Jon, Oct 29: one message for the Pro launch and the updated Terms of Use.
const ANNOUNCEMENT_ID = 'pro-open-2026-11';

// The same end as the app banners' `until` (ocl_online#604).
export const ANNOUNCEMENT_ENDS_AT = Date.parse('2026-11-09T00:00:00Z');

// "Learn more" goes to the Pro launch post (ocl_online#325), published by
// content/blog/2026-11-01-ocl-online-pro-is-open.md (ocl_online#212). False sends it
// to the September letter instead.
const LAUNCH_POST_LIVE = true;

// Relative on purpose: the site is served from preview.openconceptlab.org today and from
// openconceptlab.org after the DNS flip, so an absolute host would break on one of them.
// Each link records its own click (link_url), so GA tells the two apart.
export const ANNOUNCEMENT_LINKS = [
  { labelKey: 'announcement.learn_more_label', to: LAUNCH_POST_LIVE ? '/blog/ocl-online-pro-is-open' : '/blog/letter-to-the-ocl-community' },
  { labelKey: 'announcement.terms_label', to: '/terms-of-use' },
];

const DISMISSED_KEY = 'announcementDismissed';

const BANNER_ID = 'announcement-banner';
const ENDED_ATTRIBUTE = 'data-announcement-ended';

// The longest wait between checks of the end, so a timer that a sleeping laptop
// or a clock change delays can't keep an ended banner up for long.
const MAX_DELAY = 60 * 1000;

const hasEnded = () => Date.now() >= ANNOUNCEMENT_ENDS_AT;

// Takes the banner down on a page left open across the end.
const subscribeToEnd = onChange => {
  let timer;
  const schedule = () => {
    const remaining = ANNOUNCEMENT_ENDS_AT - Date.now();
    if (remaining > 0)
      timer = setTimeout(() => {
        onChange();
        schedule();
      }, Math.min(remaining, MAX_DELAY));
  };
  schedule();
  return () => clearTimeout(timer);
};

// What the prerendered HTML holds. A build after the end leaves the banner out.
// In the browser the first render has to match the HTML it's given, and a page
// built before the end still has the banner, so ask the page: React removes the
// banner right after hydrating. Until then HIDE_ONCE_ENDED keeps it from showing.
const endedInHtml = () => (globalThis.__SSR__ ? hasEnded() : !document.getElementById(BANNER_ID));

// Inline so it runs while the HTML is parsed, ahead of the banner's markup: a page
// built before the end never paints the banner once the end has passed, even
// before the bundle loads. The hidden banner takes no space, so nothing jumps when
// React removes it.
const HIDE_ONCE_ENDED = `if(Date.now()>=${ANNOUNCEMENT_ENDS_AT})document.documentElement.setAttribute('${ENDED_ATTRIBUTE}','')`;

const AnnouncementBanner = () => {
  const { t } = useTranslation();
  const ended = React.useSyncExternalStore(subscribeToEnd, hasEnded, endedInHtml);
  // Deliberately not a lazy useState initializer reading localStorage
  // straight away: that runs on the client's very first (hydration) render
  // too, and a returning visitor who already dismissed this exact
  // announcement would disagree with the server's always-shown prerendered
  // HTML — a structural mismatch (whole banner present vs. absent) severe
  // enough that React abandons the prerendered HTML instead of hydrating
  // it (errors 418/423). Defaulting to shown here matches what SSR always
  // renders; the effect corrects it right after mount, a normal
  // post-hydration update rather than a hydration diff.
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY) === ANNOUNCEMENT_ID)
      setDismissed(true);
  }, []);

  if (ended || dismissed)
    return null;

  const onDismiss = () => {
    GAService.recordAnnouncementDismiss(ANNOUNCEMENT_ID);
    localStorage.setItem(DISMISSED_KEY, ANNOUNCEMENT_ID);
    setDismissed(true);
  };

  // Safe to record inline because the links are in-app routes: React Router
  // handles the click without a document unload, so the event is not racing
  // a navigation the way it would be on an external href.
  const onLinkClick = to => () => GAService.recordAnnouncementClick(ANNOUNCEMENT_ID, to);

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: HIDE_ONCE_ENDED }} />
      <Box id={BANNER_ID} sx={{ backgroundColor: 'primary.95', py: 1, [`html[${ENDED_ATTRIBUTE}] &`]: { display: 'none' } }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <CampaignOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'surface.dark' }}>
              {t('announcement.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
              {ANNOUNCEMENT_LINKS.map(({ labelKey, to }, i) => (
                <React.Fragment key={to}>
                  {i ? ' · ' : ' '}
                  <Link component={RouterLink} to={to} onClick={onLinkClick(to)} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {t(labelKey)}
                  </Link>
                </React.Fragment>
              ))}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('announcement.dismiss_aria')} onClick={onDismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Container>
      </Box>
    </>
  );
};

export default AnnouncementBanner;
